import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';

const TIPOS = [
  { value: 'reparacion',    label: 'Reparación' },
  { value: 'modernizacion', label: 'Modernización' },
  { value: 'normativo',     label: 'Normativo' },
];

const PREFIJO_TIPO = { reparacion: 'REP', modernizacion: 'MODER', normativo: 'NCH' };
const STOP_WORDS_COM = new Set(['COMUNIDAD', 'EDIFICIO']);
const MAX_COM = 12;

function calcularSufijo(comunidad) {
  if (!comunidad) return '';
  const limpio = comunidad
    .toUpperCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^A-Z0-9 ]/g, '')
    .trim();
  if (!limpio) return '';
  const palabras = limpio.split(/ +/).filter(p => !STOP_WORDS_COM.has(p));
  if (!palabras.length) return '';
  const sinEspacios = palabras.join('');
  if (sinEspacios.length <= MAX_COM) return sinEspacios;
  return palabras.map(p => p.slice(0, 3)).join('').slice(0, MAX_COM);
}

const CLP = (n) =>
  Number(n || 0).toLocaleString('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 });

const ITEM_VACIO = {
  catalogo_id: null,
  descripcion: '',
  cantidad: 1,
  precio_unitario: 0,
  porcentaje_utilidad: 0,
  descuento_linea: 0,
  comentario: '',
};

const INPUT_ST = {
  padding: '10px 12px', border: '1.5px solid #2E4A40', borderRadius: 8,
  fontSize: 14, background: '#0D1311', color: '#F4F7F5',
  boxSizing: 'border-box', fontFamily: 'inherit',
};
const LABEL_ST = {
  display: 'block', fontSize: 13, fontWeight: 600, color: '#9FAAA5', marginBottom: 5,
};
const CARD = {
  background: '#161C19', borderRadius: 12, padding: 20, marginBottom: 16,
  border: '1px solid rgba(255,255,255,0.07)',
};
const CELL_INPUT = {
  width: '100%', padding: '6px 8px', border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 6, fontSize: 13, boxSizing: 'border-box',
  background: '#0D1311', color: '#F4F7F5', fontFamily: 'inherit',
};

function generarTextoTecnico(plazoDias, garantiaAnos, adicional) {
  const lineas = [];
  if (plazoDias) {
    const d = parseInt(plazoDias) || 0;
    lineas.push(`Plazo de ejecucion de ${d} ${d === 1 ? 'día' : 'días'}`);
  }
  const ga = parseInt(garantiaAnos) || 1;
  lineas.push(`Garantia Tecnica de ${ga} año${ga !== 1 ? 's' : ''}`);
  if (adicional?.trim()) lineas.push(adicional.trim());
  return lineas.join('\n');
}

function generarTextoPago(tipoPago, porcentajeInicial, nCuotas, baseNeta) {
  if (tipoPago !== 'cuotas') {
    return 'Facturacion 100%, termino de trabajos';
  }
  const pct      = parseFloat(porcentajeInicial) || 0;
  const n        = parseInt(nCuotas) || 0;
  const valInicial  = baseNeta * pct / 100;
  const valRestante = baseNeta * (100 - pct) / 100;
  const valCuota    = n > 0 ? valRestante / n : 0;
  const restante    = 100 - pct;
  const l1 = `- Facturacion ${pct}% inicio de trabajos, por ${CLP(valInicial)} + IVA`;
  const l2 = `- Facturacion ${restante}%, termino de trabajos, en ${n} cuota${n !== 1 ? 's' : ''} de ${CLP(valCuota)} + IVA`;
  return `${l1}\n${l2}`;
}

function subtotalItem(it) {
  const cant = parseFloat(it.cantidad) || 0;
  const pu   = parseFloat(it.precio_unitario) || 0;
  const util = parseFloat(it.porcentaje_utilidad) || 0;
  const dto  = parseFloat(it.descuento_linea) || 0;
  return cant * pu * (1 + util / 100) * (1 - dto / 100);
}

function utilidadItem(it) {
  const cant = parseFloat(it.cantidad) || 0;
  const pu   = parseFloat(it.precio_unitario) || 0;
  const util = parseFloat(it.porcentaje_utilidad) || 0;
  const dto  = parseFloat(it.descuento_linea) || 0;
  return cant * pu * (util / 100) * (1 - dto / 100);
}

export default function FormPresupuesto({ id, onGuardado, onCancelar }) {
  const esEdicion = !!id;

  const [comunidad, setComunidad] = useState('');
  const [comunidades, setComunidades] = useState([]);
  const [ascensoresComunidad, setAscensoresComunidad] = useState([]);
  const [ascensoresSeleccionados, setAscensoresSeleccionados] = useState([]);
  const [tipo, setTipo] = useState('reparacion');
  const [comentario, setComentario] = useState('');
  const [notas, setNotas] = useState('');
  const [plazoDias, setPlazoDias] = useState('');
  const [garantiaAnos, setGarantiaAnos] = useState(1);
  const [condTecnicaExtra, setCondTecnicaExtra] = useState('');
  const [tipoPago, setTipoPago] = useState('completo');
  const [porcentajeInicial, setPorcentajeInicial] = useState('');
  const [nCuotas, setNCuotas] = useState('');
  const [sufijoComunidad, setSufijoComunidad] = useState('');
  const [nombre, setNombre] = useState('');
  const [nombreAlerta, setNombreAlerta] = useState('');
  const [descuento, setDescuento] = useState(0);
  const [items, setItems] = useState([]);
  const [ivaPct, setIvaPct] = useState(19);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const [buscadorCatalogo, setBuscadorCatalogo] = useState('');
  const [resultadosCatalogo, setResultadosCatalogo] = useState([]);
  const [mostrarBuscador, setMostrarBuscador] = useState(false);
  const [itemBuscadorIdx, setItemBuscadorIdx] = useState(null);

  useEffect(() => {
    api.get('/catalogo/configuracion').then(r => {
      const iva = r.data.find(c => c.clave === 'iva_pct');
      if (iva) setIvaPct(parseFloat(iva.valor));
    }).catch(() => {});

    api.get('/ascensores').then(r => {
      const unicas = [...new Set(r.data.map(a => a.comunidad).filter(Boolean))].sort();
      setComunidades(unicas);
    }).catch(() => {});

    if (esEdicion) {
      api.get(`/presupuestos/${id}`).then(r => {
        const p = r.data;
        setComunidad(p.comunidad || '');
        setTipo(p.tipo || 'reparacion');
        setComentario(p.comentario || '');
        setNotas(p.notas || '');
        setTipoPago(p.tipo_pago || 'completo');
        setPorcentajeInicial(p.porcentaje_inicial || '');
        setNCuotas(p.n_cuotas || '');
        setNombre(p.nombre || '');
        setDescuento(p.descuento_pct || 0);
        setPlazoDias(String(p.plazo_dias || ''));
        setGarantiaAnos(p.garantia_anos || 1);
        setItems(p.items.map(it => ({
          catalogo_id: it.catalogo_id,
          descripcion: it.descripcion,
          cantidad: it.cantidad,
          precio_unitario: it.precio_unitario,
          porcentaje_utilidad: it.porcentaje_utilidad || 0,
          descuento_linea: it.descuento_linea,
          comentario: it.comentario || '',
        })));
        setAscensoresSeleccionados(p.ascensores.map(a => a.id));
      }).catch(() => setError('Error al cargar presupuesto'));
    }
  }, [id, esEdicion]);

  useEffect(() => {
    if (!comunidad) { setAscensoresComunidad([]); return; }
    api.get('/ascensores', { params: { q: comunidad } }).then(r => {
      const filtrados = r.data.filter(a => a.comunidad === comunidad);
      setAscensoresComunidad(filtrados);
    }).catch(() => {});
  }, [comunidad]);

  useEffect(() => {
    if (!esEdicion) setSufijoComunidad(calcularSufijo(comunidad));
  }, [comunidad, esEdicion]);

  const buscarCatalogo = useCallback(async (q) => {
    if (!q.trim()) { setResultadosCatalogo([]); return; }
    try {
      const r = await api.get('/catalogo', { params: { q, activo: 'true' } });
      setResultadosCatalogo(r.data.slice(0, 8));
    } catch { setResultadosCatalogo([]); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => buscarCatalogo(buscadorCatalogo), 250);
    return () => clearTimeout(t);
  }, [buscadorCatalogo, buscarCatalogo]);

  const agregarItemLibre = () => {
    setItems(prev => [...prev, { ...ITEM_VACIO }]);
  };

  const verificarNombre = useCallback(async (val) => {
    if (!val.trim()) { setNombreAlerta(''); return; }
    try {
      const r = await api.get('/presupuestos', { params: { nombre: val.trim() } });
      const coincide = r.data.find(p =>
        p.nombre?.toLowerCase() === val.trim().toLowerCase() && p.id !== id
      );
      setNombreAlerta(coincide ? `Ya existe un presupuesto con este nombre (${coincide.numero})` : '');
    } catch { setNombreAlerta(''); }
  }, [id]);

  useEffect(() => {
    const t = setTimeout(() => verificarNombre(nombre), 400);
    return () => clearTimeout(t);
  }, [nombre, verificarNombre]);

  const agregarDesdeCatalogo = (catalogo) => {
    setItems(prev => {
      const nuevos = [...prev];
      const nuevo = {
        catalogo_id: catalogo.id,
        descripcion: catalogo.nombre,
        cantidad: 1,
        precio_unitario: parseFloat(catalogo.precio_ref) || 0,
        porcentaje_utilidad: 0,
        descuento_linea: 0,
        comentario: '',
      };
      if (itemBuscadorIdx !== null) {
        nuevos[itemBuscadorIdx] = nuevo;
      } else {
        nuevos.push(nuevo);
      }
      return nuevos;
    });
    setMostrarBuscador(false);
    setItemBuscadorIdx(null);
    setBuscadorCatalogo('');
    setResultadosCatalogo([]);
  };

  const actualizarItem = (idx, campo, valor) => {
    setItems(prev => {
      const copia = [...prev];
      copia[idx] = { ...copia[idx], [campo]: valor };
      return copia;
    });
  };

  const eliminarItem = (idx) => {
    if (!window.confirm('¿Eliminar este ítem?')) return;
    setItems(prev => prev.filter((_, i) => i !== idx));
  };

  const moverItem = (idx, dir) => {
    setItems(prev => {
      const copia = [...prev];
      const destino = idx + dir;
      if (destino < 0 || destino >= copia.length) return copia;
      [copia[idx], copia[destino]] = [copia[destino], copia[idx]];
      return copia;
    });
  };

  const subtotal        = items.reduce((acc, it) => acc + subtotalItem(it), 0);
  const utilidadTotal   = items.reduce((acc, it) => acc + utilidadItem(it), 0);
  const dto             = subtotal * (parseFloat(descuento) || 0) / 100;
  const netoConDescuento = subtotal - dto;   // base para IVA y condiciones de pago (cuotas)
  const iva             = netoConDescuento * (ivaPct / 100);
  const total           = netoConDescuento + iva;

  const toggleAscensor = (aId) => {
    setAscensoresSeleccionados(prev =>
      prev.includes(aId) ? prev.filter(x => x !== aId) : [...prev, aId]
    );
  };

  const guardar = async (enviar = false) => {
    if (!tipo) { setError('El tipo es requerido'); return; }
    if (items.length === 0) { setError('Debes agregar al menos un ítem'); return; }
    for (const it of items) {
      if (!it.descripcion?.trim()) { setError('Todos los ítems deben tener descripción'); return; }
    }
    setGuardando(true);
    setError('');
    try {
      const payload = {
        comunidad: comunidad || null,
        nombre_cliente: ascensoresComunidad[0]?.nombre_cliente || null,
        sufijo_comunidad: sufijoComunidad || null,
        tipo,
        comentario: comentario || null,
        notas: notas || null,
        condiciones_pago: generarTextoPago(tipoPago, porcentajeInicial, nCuotas, netoConDescuento),
        condiciones_tecnicas: generarTextoTecnico(plazoDias, garantiaAnos, condTecnicaExtra),
        tipo_pago: tipoPago,
        porcentaje_inicial: tipoPago === 'cuotas' ? (parseFloat(porcentajeInicial) || 0) : 0,
        n_cuotas: tipoPago === 'cuotas' ? (parseInt(nCuotas) || null) : null,
        nombre: nombre.trim() || null,
        descuento_pct: parseInt(descuento) || 0,
        plazo_dias: parseInt(plazoDias) || null,
        garantia_anos: parseInt(garantiaAnos) || 1,
        items: items.map(it => ({
          catalogo_id: it.catalogo_id || null,
          descripcion: it.descripcion.trim(),
          cantidad: parseFloat(it.cantidad) || 1,
          precio_unitario: parseFloat(it.precio_unitario) || 0,
          porcentaje_utilidad: parseFloat(it.porcentaje_utilidad) || 0,
          descuento_linea: parseFloat(it.descuento_linea) || 0,
          comentario: it.comentario || null,
        })),
        ascensor_ids: ascensoresSeleccionados,
      };

      let pId = id;
      if (esEdicion) {
        await api.put(`/presupuestos/${id}`, payload);
      } else {
        const r = await api.post('/presupuestos', payload);
        pId = r.data.id;
      }

      if (enviar) {
        await api.put(`/presupuestos/${pId}/estado`, { estado: 'enviado' });
      }

      onGuardado();
    } catch (e) {
      setError(e.response?.data?.error || 'Error al guardar');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      {/* Cabecera */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <button onClick={onCancelar}
          style={{ background: 'none', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8,
                   padding: '6px 14px', cursor: 'pointer', fontSize: 14,
                   color: '#C9D3CE', fontFamily: 'inherit' }}>
          ← Volver
        </button>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#F4F7F5' }}>
          {esEdicion ? 'Editar presupuesto' : 'Nuevo presupuesto'}
        </h2>
      </div>

      {error && (
        <div style={{ background: '#2D0F0F', color: '#EF4444', borderRadius: 8,
                      padding: '10px 14px', marginBottom: 16, fontSize: 14,
                      border: '1px solid rgba(198,40,40,0.2)' }}>
          {error}
        </div>
      )}

      {/* Datos generales */}
      <div style={CARD}>
        <h3 style={{ margin: '0 0 16px', fontSize: 15, color: '#F4F7F5' }}>Datos generales</h3>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 14 }}>
          <div>
            <label style={LABEL_ST}>Comunidad</label>
            <select value={comunidad} onChange={e => { setComunidad(e.target.value); setAscensoresSeleccionados([]); }}
              style={{ ...INPUT_ST, width: '100%' }}>
              <option value="">Sin comunidad</option>
              {comunidades.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            {ascensoresComunidad[0]?.nombre_cliente && (
              <p style={{ margin: '6px 0 0', fontSize: 12, color: '#4CAF50' }}>
                Cliente en PDF: <strong>{ascensoresComunidad[0].nombre_cliente}</strong>
              </p>
            )}
          </div>
          <div>
            <label style={LABEL_ST}>Tipo *</label>
            <select value={tipo} onChange={e => setTipo(e.target.value)}
              style={{ ...INPUT_ST, width: '100%' }}>
              {TIPOS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
        </div>

        {/* Vista previa editable del código — solo en creación */}
        {!esEdicion && (
          <div style={{ marginBottom: 14, background: '#0D1311', borderRadius: 8,
                        padding: '10px 14px', display: 'flex', alignItems: 'center',
                        gap: 0, border: '1px solid #2E4A40' }}>
            <span style={{ fontSize: 12, color: '#6B7C74', marginRight: 8, whiteSpace: 'nowrap' }}>
              Código:
            </span>
            <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 14,
                           color: '#4CAF50', whiteSpace: 'nowrap' }}>
              {PREFIJO_TIPO[tipo] || 'REP'}&nbsp;—&nbsp;###&nbsp;—&nbsp;
            </span>
            <input
              value={sufijoComunidad}
              onChange={e => setSufijoComunidad(
                e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, MAX_COM)
              )}
              placeholder="SUFIJO"
              style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 14, color: '#4CAF50',
                       border: 'none', borderBottom: '2px solid #4CAF50', background: 'transparent',
                       outline: 'none', width: `${Math.max(6, sufijoComunidad.length + 2)}ch`,
                       padding: '0 2px' }}
            />
            <span style={{ fontSize: 11, color: '#6B7C74', marginLeft: 10 }}>
              ### se asigna al guardar
            </span>
          </div>
        )}

        {/* Nombre personalizado */}
        <div style={{ marginBottom: 14 }}>
          <label style={LABEL_ST}>
            Nombre del presupuesto{' '}
            <span style={{ fontWeight: 400, color: '#6B7C74' }}>(opcional)</span>
          </label>
          <input value={nombre} onChange={e => setNombre(e.target.value)}
            placeholder="Nombre personalizado — si se deja vacío, se usa el código autogenerado"
            style={{ ...INPUT_ST, width: '100%', border: `1.5px solid ${nombreAlerta ? '#F59E0B' : '#2E4A40'}` }} />
          {nombreAlerta && (
            <p style={{ margin: '4px 0 0', fontSize: 12, color: '#F59E0B' }}>{nombreAlerta}</p>
          )}
        </div>

        {/* Ascensores */}
        {comunidad && ascensoresComunidad.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <label style={LABEL_ST}>
              Ascensores ({ascensoresSeleccionados.length} seleccionados)
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {ascensoresComunidad.map(a => {
                const sel = ascensoresSeleccionados.includes(a.id);
                return (
                  <button key={a.id} onClick={() => toggleAscensor(a.id)}
                    style={{ padding: '6px 12px', borderRadius: 20,
                             border: `1.5px solid ${sel ? '#4CAF50' : '#2E4A40'}`,
                             background: sel ? '#12382D' : '#0D1311',
                             color: sel ? '#4CAF50' : '#8A9994',
                             cursor: 'pointer', fontSize: 13, fontWeight: sel ? 600 : 400,
                             fontFamily: 'inherit' }}>
                    {a.label} {a.torre ? `(T${a.torre})` : ''}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Comentario general */}
        <div>
          <label style={LABEL_ST}>Descripción del trabajo</label>
          <textarea value={comentario} onChange={e => setComentario(e.target.value)}
            rows={3} placeholder="Descripción general de los trabajos a realizar…"
            style={{ ...INPUT_ST, width: '100%', resize: 'vertical' }} />
        </div>
      </div>

      {/* Ítems */}
      <div style={CARD}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 15, color: '#F4F7F5' }}>Ítems ({items.length})</h3>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => { setMostrarBuscador(true); setItemBuscadorIdx(null); }}
              style={{ padding: '7px 14px', border: '1.5px solid #2E4A40', borderRadius: 8,
                       background: '#0D1311', color: '#4CAF50', cursor: 'pointer',
                       fontSize: 13, fontWeight: 600, fontFamily: 'inherit' }}>
              + Desde catálogo
            </button>
            <button onClick={agregarItemLibre}
              style={{ padding: '7px 14px', border: '1.5px solid rgba(255,255,255,0.12)', borderRadius: 8,
                       background: 'transparent', color: '#8A9994', cursor: 'pointer',
                       fontSize: 13, fontWeight: 600, fontFamily: 'inherit' }}>
              + Ítem libre
            </button>
          </div>
        </div>

        {/* Buscador catálogo */}
        {mostrarBuscador && (
          <div style={{ background: '#0D1311', borderRadius: 10, padding: 14, marginBottom: 16,
                        border: '1px solid #2E4A40' }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
              <input autoFocus value={buscadorCatalogo}
                onChange={e => setBuscadorCatalogo(e.target.value)}
                placeholder="Buscar en catálogo…"
                style={{ ...INPUT_ST, flex: 1, padding: '9px 12px' }} />
              <button onClick={() => { setMostrarBuscador(false); setBuscadorCatalogo(''); setResultadosCatalogo([]); }}
                style={{ padding: '9px 14px', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8,
                         background: '#161C19', color: '#8A9994', cursor: 'pointer', fontSize: 14,
                         fontFamily: 'inherit' }}>
                ✕
              </button>
            </div>
            {resultadosCatalogo.length > 0 && (
              <div style={{ background: '#161C19', borderRadius: 8,
                            border: '1px solid rgba(255,255,255,0.07)',
                            maxHeight: 220, overflowY: 'auto' }}>
                {resultadosCatalogo.map(cat => (
                  <div key={cat.id}
                    onClick={() => agregarDesdeCatalogo(cat)}
                    style={{ padding: '10px 14px', cursor: 'pointer',
                             borderBottom: '1px solid rgba(255,255,255,0.05)',
                             display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#6B7C74', marginRight: 8 }}>
                        {cat.codigo}
                      </span>
                      <span style={{ fontSize: 14, color: '#F4F7F5' }}>{cat.nombre}</span>
                    </div>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#4CAF50' }}>
                      {CLP(cat.precio_ref)}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {buscadorCatalogo && resultadosCatalogo.length === 0 && (
              <p style={{ color: '#6B7C74', fontSize: 13, margin: 0 }}>Sin resultados</p>
            )}
          </div>
        )}

        {/* Tabla de ítems */}
        {items.length === 0 ? (
          <p style={{ color: '#6B7C74', textAlign: 'center', padding: '20px 0' }}>
            Sin ítems. Agrega desde catálogo o crea un ítem libre.
          </p>
        ) : (
          <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid rgba(255,255,255,0.06)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#0D1311' }}>
                  <th style={{ padding: '8px 8px', textAlign: 'left', fontWeight: 600, color: '#9FAAA5', width: 32,
                               borderBottom: '1px solid rgba(255,255,255,0.07)' }}>#</th>
                  <th style={{ padding: '8px 8px', textAlign: 'left', fontWeight: 600, color: '#9FAAA5',
                               borderBottom: '1px solid rgba(255,255,255,0.07)' }}>Descripción</th>
                  <th style={{ padding: '8px 8px', textAlign: 'right', fontWeight: 600, color: '#9FAAA5', width: 60,
                               borderBottom: '1px solid rgba(255,255,255,0.07)' }}>Cant.</th>
                  <th style={{ padding: '8px 8px', textAlign: 'right', fontWeight: 600, color: '#9FAAA5', width: 100,
                               borderBottom: '1px solid rgba(255,255,255,0.07)' }}>Costo</th>
                  <th style={{ padding: '8px 8px', textAlign: 'right', fontWeight: 600, color: '#4CAF50', width: 62,
                               borderBottom: '1px solid rgba(255,255,255,0.07)' }}>Util%</th>
                  <th style={{ padding: '8px 8px', textAlign: 'right', fontWeight: 600, color: '#9FAAA5', width: 60,
                               borderBottom: '1px solid rgba(255,255,255,0.07)' }}>Dto%</th>
                  <th style={{ padding: '8px 8px', textAlign: 'right', fontWeight: 600, color: '#9FAAA5', width: 110,
                               borderBottom: '1px solid rgba(255,255,255,0.07)' }}>Subtotal</th>
                  <th style={{ padding: '8px 8px', width: 80,
                               borderBottom: '1px solid rgba(255,255,255,0.07)' }}></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => (
                  <React.Fragment key={idx}>
                    <tr style={{ background: idx % 2 === 0 ? '#111714' : '#0D1311',
                                 borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                      <td style={{ padding: '6px 8px', color: '#6B7C74', fontSize: 12, textAlign: 'center' }}>
                        {idx + 1}
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <input value={it.descripcion}
                          onChange={e => actualizarItem(idx, 'descripcion', e.target.value)}
                          placeholder="Descripción del ítem"
                          style={CELL_INPUT} />
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <input type="number" min="0" step="1" value={it.cantidad}
                          onKeyDown={e => (e.key === '.' || e.key === ',') && e.preventDefault()}
                          onChange={e => actualizarItem(idx, 'cantidad', e.target.value)}
                          style={{ ...CELL_INPUT, textAlign: 'right' }} />
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <input type="number" min="0" step="100" value={it.precio_unitario}
                          onChange={e => actualizarItem(idx, 'precio_unitario', e.target.value)}
                          style={{ ...CELL_INPUT, textAlign: 'right' }} />
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                          <input type="number" min="0" max="999" step="1" value={it.porcentaje_utilidad}
                            onKeyDown={e => (e.key === '.' || e.key === ',') && e.preventDefault()}
                            onChange={e => actualizarItem(idx, 'porcentaje_utilidad', e.target.value)}
                            style={{ ...CELL_INPUT, textAlign: 'right',
                                     border: '1.5px solid rgba(79,175,80,0.3)', color: '#4CAF50' }} />
                          {parseFloat(it.porcentaje_utilidad) > 0 && (
                            <span style={{ fontSize: 10, color: '#4CAF50', whiteSpace: 'nowrap' }}>
                              +{CLP(utilidadItem(it))}
                            </span>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <input type="number" min="0" max="100" step="1" value={it.descuento_linea}
                          onKeyDown={e => (e.key === '.' || e.key === ',') && e.preventDefault()}
                          onChange={e => actualizarItem(idx, 'descuento_linea', e.target.value)}
                          style={{ ...CELL_INPUT, textAlign: 'right' }} />
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600, color: '#F4F7F5' }}>
                        {CLP(subtotalItem(it))}
                      </td>
                      <td style={{ padding: '6px 8px' }}>
                        <div style={{ display: 'flex', gap: 3 }}>
                          <button onClick={() => moverItem(idx, -1)} disabled={idx === 0}
                            style={{ padding: '3px 6px', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 4,
                                     background: '#0D1311', color: '#8A9994', cursor: 'pointer', fontSize: 11,
                                     fontFamily: 'inherit' }}>↑</button>
                          <button onClick={() => moverItem(idx, 1)} disabled={idx === items.length - 1}
                            style={{ padding: '3px 6px', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 4,
                                     background: '#0D1311', color: '#8A9994', cursor: 'pointer', fontSize: 11,
                                     fontFamily: 'inherit' }}>↓</button>
                          <button onClick={() => eliminarItem(idx)}
                            style={{ padding: '3px 6px', border: '1px solid rgba(198,40,40,0.2)', borderRadius: 4,
                                     background: '#2D0F0F', color: '#EF4444', cursor: 'pointer', fontSize: 11,
                                     fontFamily: 'inherit' }}>✕</button>
                        </div>
                      </td>
                    </tr>
                    {/* Fila comentario por ítem */}
                    <tr style={{ background: idx % 2 === 0 ? '#111714' : '#0D1311',
                                 borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                      <td></td>
                      <td colSpan={7} style={{ padding: '0 8px 8px' }}>
                        <input value={it.comentario}
                          onChange={e => actualizarItem(idx, 'comentario', e.target.value)}
                          placeholder="Comentario específico de este ítem (opcional)"
                          style={{ ...CELL_INPUT, fontSize: 12, color: '#8A9994',
                                   background: 'transparent' }} />
                      </td>
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Resumen + Notas */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        {/* Notas y condiciones */}
        <div style={CARD}>
          <h3 style={{ margin: '0 0 14px', fontSize: 15, color: '#F4F7F5' }}>Notas y condiciones</h3>

          <label style={LABEL_ST}>Notas</label>
          <textarea value={notas} onChange={e => setNotas(e.target.value)} rows={2}
            placeholder="Notas adicionales para el cliente…"
            style={{ ...INPUT_ST, width: '100%', resize: 'vertical', marginBottom: 14 }} />

          <label style={LABEL_ST}>Forma de pago</label>
          <div style={{ display: 'flex', gap: 16, marginBottom: tipoPago === 'cuotas' ? 10 : 14,
                        alignItems: 'center' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                            fontSize: 14, color: '#C9D3CE' }}>
              <input type="radio" value="completo" checked={tipoPago === 'completo'}
                onChange={() => setTipoPago('completo')} /> Pago completo
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                            fontSize: 14, color: '#C9D3CE' }}>
              <input type="radio" value="cuotas" checked={tipoPago === 'cuotas'}
                onChange={() => setTipoPago('cuotas')} /> Pago en cuotas
            </label>
          </div>
          {tipoPago === 'cuotas' && (() => {
            const pct      = parseFloat(porcentajeInicial) || 0;
            const n        = parseInt(nCuotas) || 0;
            const valInicial  = netoConDescuento * pct / 100;
            const valRestante = netoConDescuento * (100 - pct) / 100;
            const valCuota    = n > 0 ? valRestante / n : 0;
            return (
              <div style={{ background: '#0D1311', borderRadius: 8, padding: '12px 14px',
                            marginBottom: 14, display: 'flex', flexDirection: 'column', gap: 12,
                            border: '1px solid #2E4A40' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <label style={{ fontSize: 13, color: '#9FAAA5', whiteSpace: 'nowrap', minWidth: 110 }}>
                    % inicial:
                  </label>
                  <input type="number" min="0" max="99" step="5" value={porcentajeInicial}
                    onChange={e => setPorcentajeInicial(e.target.value)}
                    placeholder="Ej: 30"
                    style={{ width: 64, padding: '7px 8px', border: '1.5px solid #2E4A40',
                             borderRadius: 8, fontSize: 14, textAlign: 'center',
                             background: '#161C19', color: '#F4F7F5', fontFamily: 'inherit' }} />
                  <span style={{ fontSize: 13, color: '#9FAAA5' }}>%</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#4CAF50', marginLeft: 4 }}>
                    = {CLP(valInicial)}
                    <span style={{ fontSize: 11, fontWeight: 400, color: '#6B7C74', marginLeft: 4 }}>+ IVA</span>
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <label style={{ fontSize: 13, color: '#9FAAA5', whiteSpace: 'nowrap', minWidth: 110 }}>
                    N° cuotas:
                  </label>
                  <input type="number" min="1" max="60" value={nCuotas}
                    onChange={e => setNCuotas(e.target.value)}
                    placeholder="Ej: 3"
                    style={{ width: 64, padding: '7px 8px', border: '1.5px solid #2E4A40',
                             borderRadius: 8, fontSize: 14, textAlign: 'center',
                             background: '#161C19', color: '#F4F7F5', fontFamily: 'inherit' }} />
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#F4F7F5', marginLeft: 4 }}>
                    = {CLP(valCuota)} c/u
                    <span style={{ fontSize: 11, fontWeight: 400, color: '#6B7C74', marginLeft: 4 }}>+ IVA</span>
                  </span>
                </div>
              </div>
            );
          })()}

          <label style={LABEL_ST}>
            Condiciones de Pago
            <span style={{ fontWeight: 400, color: '#6B7C74', marginLeft: 6, fontSize: 11 }}>auto-generado</span>
          </label>
          <div style={{ width: '100%', padding: '10px 12px', border: '1px solid rgba(255,255,255,0.07)',
                        borderRadius: 8, fontSize: 13, background: '#0D1311', marginBottom: 14,
                        color: '#C9D3CE', lineHeight: 1.7, whiteSpace: 'pre-line', boxSizing: 'border-box' }}>
            {generarTextoPago(tipoPago, porcentajeInicial, nCuotas, netoConDescuento)}
          </div>

          <label style={LABEL_ST}>
            Condiciones Técnicas
            <span style={{ fontWeight: 400, color: '#6B7C74', marginLeft: 6, fontSize: 11 }}>auto-generado</span>
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <label style={{ fontSize: 13, color: '#9FAAA5', whiteSpace: 'nowrap', minWidth: 140 }}>
                Plazo de ejecución:
              </label>
              <input type="number" min="1" value={plazoDias}
                onChange={e => setPlazoDias(e.target.value)}
                placeholder="Ej: 30"
                style={{ width: 72, padding: '7px 8px', border: '1.5px solid #2E4A40',
                         borderRadius: 8, fontSize: 14, textAlign: 'center',
                         background: '#0D1311', color: '#F4F7F5', fontFamily: 'inherit' }} />
              <span style={{ fontSize: 13, color: '#9FAAA5' }}>días</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <label style={{ fontSize: 13, color: '#9FAAA5', whiteSpace: 'nowrap', minWidth: 140 }}>
                Garantía técnica:
              </label>
              <input type="number" min="1" value={garantiaAnos}
                onChange={e => setGarantiaAnos(e.target.value)}
                style={{ width: 72, padding: '7px 8px', border: '1.5px solid #2E4A40',
                         borderRadius: 8, fontSize: 14, textAlign: 'center',
                         background: '#0D1311', color: '#F4F7F5', fontFamily: 'inherit' }} />
              <span style={{ fontSize: 13, color: '#9FAAA5' }}>año(s)</span>
            </div>
          </div>
          <textarea value={condTecnicaExtra} onChange={e => setCondTecnicaExtra(e.target.value)}
            rows={2} placeholder="Condiciones adicionales (opcional)…"
            style={{ ...INPUT_ST, width: '100%', resize: 'vertical', marginBottom: 8 }} />
          <div style={{ padding: '10px 12px', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 8,
                        fontSize: 13, background: '#0D1311', color: '#C9D3CE',
                        lineHeight: 1.7, whiteSpace: 'pre-line' }}>
            {generarTextoTecnico(plazoDias, garantiaAnos, condTecnicaExtra)}
          </div>
        </div>

        {/* Resumen montos */}
        <div style={CARD}>
          <h3 style={{ margin: '0 0 14px', fontSize: 15, color: '#F4F7F5' }}>Resumen</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
              <span style={{ color: '#8A9994' }}>Subtotal</span>
              <strong style={{ color: '#F4F7F5' }}>{CLP(subtotal)}</strong>
            </div>
            {utilidadTotal > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13,
                            background: '#12382D', borderRadius: 6, padding: '6px 8px' }}>
                <span style={{ color: '#4CAF50', fontWeight: 600 }}>Utilidad estimada</span>
                <strong style={{ color: '#4CAF50' }}>{CLP(utilidadTotal)}</strong>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between',
                          alignItems: 'center', fontSize: 14 }}>
              <span style={{ color: '#8A9994' }}>Descuento global</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <input type="number" min="0" max="100" step="1" value={descuento}
                  onKeyDown={e => (e.key === '.' || e.key === ',') && e.preventDefault()}
                  onChange={e => setDescuento(Math.min(100, Math.max(0, parseInt(e.target.value) || 0)))}
                  style={{ width: 80, padding: '6px 10px', border: '1px solid #2E4A40',
                           borderRadius: 6, fontSize: 14, textAlign: 'right',
                           background: '#0D1311', color: '#F4F7F5', fontFamily: 'inherit' }} />
                <span style={{ color: '#9FAAA5', fontSize: 14 }}>%</span>
              </div>
            </div>
            {dto > 0 && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', fontSize: 12,
                            color: '#EF4444', marginTop: 2 }}>
                = -{CLP(dto)}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
              <span style={{ color: '#8A9994' }}>IVA ({ivaPct}%)</span>
              <strong style={{ color: '#F4F7F5' }}>{CLP(iva)}</strong>
            </div>
            <hr style={{ border: 'none', borderTop: '1px solid rgba(255,255,255,0.08)', margin: '4px 0' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 18, fontWeight: 700 }}>
              <span style={{ color: '#F4F7F5' }}>Total</span>
              <span style={{ color: '#4CAF50' }}>{CLP(total)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Botones */}
      <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', paddingBottom: 24 }}>
        <button onClick={onCancelar}
          style={{ padding: '10px 24px', border: '1.5px solid rgba(255,255,255,0.1)', borderRadius: 8,
                   background: 'transparent', color: '#8A9994', cursor: 'pointer', fontSize: 15,
                   fontFamily: 'inherit' }}>
          Cancelar
        </button>
        <button onClick={() => guardar(false)} disabled={guardando}
          style={{ padding: '10px 24px', border: '1.5px solid #2E4A40', borderRadius: 8,
                   background: '#0D1311', color: '#4CAF50', fontWeight: 600,
                   cursor: guardando ? 'wait' : 'pointer', fontSize: 15, fontFamily: 'inherit' }}>
          {guardando ? 'Guardando…' : 'Guardar borrador'}
        </button>
        <button onClick={() => guardar(true)} disabled={guardando}
          style={{ padding: '10px 24px', background: '#0f6e56', color: 'white',
                   border: 'none', borderRadius: 8, fontWeight: 600,
                   cursor: guardando ? 'wait' : 'pointer', fontSize: 15, fontFamily: 'inherit' }}>
          {guardando ? 'Guardando…' : 'Guardar y enviar'}
        </button>
      </div>
    </div>
  );
}
